/**
 * /sudo/businesses/$slug — single business drill-in.
 *
 * KPIs scoped to one business + member list with a per-row "Impersonate"
 * button. Impersonation itself isn't wired yet — the button is here so
 * the surface is recognisable; clicking it shows a notice + the planned
 * approach until the Better Auth admin plugin lands.
 */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, UserCog } from 'lucide-react';
import { Button } from '@bookaburra/ui';
import { useState } from 'react';
import { useDocumentTitle } from '../lib/use-document-title';
import { formatRole } from '../lib/format-role';
import { authClient } from '../auth/client';

interface AllocationInfo {
  pickedStaff: { id: string; name: string; staffType: string };
  pickedResourceIds: string[];
  tierChosen: string;
  staffStrategy: string;
  resourceStrategy: string;
  consideredStaff: { id: string; name: string; staffType: string }[];
  staffReason: string | null;
  resourceReason: string | null;
  computedAt: number | null;
}

interface RecentBooking {
  id: string;
  serviceId: string;
  serviceName: string;
  rangeStart: number;
  status: string;
  clientName: string | null;
  allocation: AllocationInfo | null;
}

interface BusinessDetail {
  tenant: {
    id: string;
    name: string;
    slug: string;
    planSlug: string;
    timezone: string;
    country: string;
    defaultLocale: string;
    address: string | null;
    createdAt: number;
  };
  kpis: {
    bookings7d: number;
    bookings30d: number;
    bookings90d: number;
    bookingsAllTime: number;
    cancellations30d: number;
    lastBookingAt: number | null;
    services: number;
    staff: number;
  };
  members: Array<{
    userId: string;
    email: string;
    name: string;
    role: 'owner' | 'manager' | 'staff' | 'client';
    memberSince: number;
  }>;
  recentBookings: RecentBooking[];
}

const STAFF_TYPE_LABEL: Record<string, string> = {
  full_time: 'Full time',
  part_time: 'Part time',
  paid_per_service: 'Paid per service',
  paid_hourly: 'Paid hourly',
};

async function fetchBusinessDetail(slug: string): Promise<BusinessDetail> {
  const res = await fetch(`/api/super/businesses/${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error('business_detail_failed');
  return res.json();
}

export const Route = createFileRoute('/sudo/businesses/$slug')({
  component: BusinessDetailPage,
});

function BusinessDetailPage() {
  const { slug } = Route.useParams();
  const query = useQuery({
    queryKey: ['sudo', 'businesses', slug],
    queryFn: () => fetchBusinessDetail(slug),
  });
  useDocumentTitle(query.data ? `${query.data.tenant.name} · super-admin` : 'Business · super-admin');

  return (
    <div className="space-y-10">
      <Link
        to="/sudo"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-4 hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to overview
      </Link>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading&hellip;</p>
      ) : query.error || !query.data ? (
        <p className="text-sm text-destructive">Failed to load business.</p>
      ) : (
        <BusinessDetailBody data={query.data} />
      )}
    </div>
  );
}

function BusinessDetailBody({ data }: { data: BusinessDetail }) {
  const { tenant, kpis, members } = data;
  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{tenant.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            /{tenant.slug} · {tenant.timezone} · {tenant.country} · joined{' '}
            {new Date(tenant.createdAt).toLocaleDateString('en-AU')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`/book/${tenant.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-medium hover:bg-secondary"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Booking page
          </a>
          <span className="inline-flex rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs font-medium uppercase tracking-wider">
            {tenant.planSlug}
          </span>
        </div>
      </header>

      <section>
        <ul className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
          <KpiCard label="Bookings · 7d" value={kpis.bookings7d} />
          <KpiCard label="Bookings · 30d" value={kpis.bookings30d} />
          <KpiCard label="Bookings · 90d" value={kpis.bookings90d} />
          <KpiCard
            label="Bookings · all-time"
            value={kpis.bookingsAllTime}
            sub={
              kpis.lastBookingAt
                ? `last: ${new Date(kpis.lastBookingAt).toLocaleDateString('en-AU')}`
                : 'no bookings yet'
            }
          />
          <KpiCard label="Cancellations · 30d" value={kpis.cancellations30d} />
          <KpiCard label="Active services" value={kpis.services} />
          <KpiCard label="Team members" value={kpis.staff} />
        </ul>
      </section>

      <section>
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold tracking-tight">Team</h2>
          <span className="text-xs text-muted-foreground">
            {members.length} {members.length === 1 ? 'member' : 'members'}
          </span>
        </div>
        <MembersTable members={members} tenantSlug={tenant.slug} />
      </section>

      <section>
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold tracking-tight">Recent bookings</h2>
          <span className="text-xs text-muted-foreground">
            Last {data.recentBookings.length} — drill in to see allocator reasoning
          </span>
        </div>
        <RecentBookingsList
          bookings={data.recentBookings}
          timezone={tenant.timezone}
        />
      </section>
    </>
  );
}

function RecentBookingsList({
  bookings,
  timezone,
}: {
  bookings: RecentBooking[];
  timezone: string;
}) {
  const [open, setOpen] = useState<string | null>(null);
  if (bookings.length === 0) {
    return (
      <p className="mt-4 text-sm text-muted-foreground">No bookings yet.</p>
    );
  }
  return (
    <ul className="mt-4 space-y-2">
      {bookings.map((b) => {
        const expanded = open === b.id;
        return (
          <li
            key={b.id}
            className="overflow-hidden rounded-2xl border border-brand-sage/15 bg-card shadow-brand"
          >
            <button
              type="button"
              onClick={() => setOpen(expanded ? null : b.id)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-secondary/40"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">
                  {b.serviceName}
                  {b.clientName ? (
                    <span className="ml-2 text-xs text-muted-foreground">
                      · {b.clientName}
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(b.rangeStart).toLocaleString('en-AU', {
                    timeZone: timezone,
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })}
                  <span className="mx-1.5 text-muted-foreground/50">·</span>
                  <span className="uppercase tracking-wider">{b.status}</span>
                </p>
              </div>
              <span className="shrink-0 text-xs font-medium text-muted-foreground">
                {expanded ? 'Hide' : 'Reasoning'}
              </span>
            </button>
            {expanded ? (
              <div className="border-t border-brand-sage/15 bg-secondary/30 px-4 py-3 text-xs">
                {b.allocation ? (
                  <AllocationReasoning info={b.allocation} />
                ) : (
                  <p className="text-muted-foreground">
                    No allocation log for this booking — pre-ADR-0014 or
                    log-write failed.
                  </p>
                )}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function AllocationReasoning({ info }: { info: AllocationInfo }) {
  return (
    <div className="space-y-3">
      <Row label="Picked">
        {info.pickedStaff.name}
        <span className="ml-2 text-muted-foreground">
          ({STAFF_TYPE_LABEL[info.pickedStaff.staffType] ?? info.pickedStaff.staffType})
        </span>
      </Row>
      <Row label="Tier">
        {STAFF_TYPE_LABEL[info.tierChosen] ?? info.tierChosen}
      </Row>
      <Row label="Strategy">
        {info.staffStrategy} <span className="text-muted-foreground/70">/</span>{' '}
        {info.resourceStrategy}
      </Row>
      {info.consideredStaff.length > 0 ? (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Considered (fallback order)
          </p>
          <ol className="mt-1 space-y-0.5">
            {info.consideredStaff.map((s, i) => (
              <li key={s.id} className="tabular-nums">
                <span className="text-muted-foreground">Rank {i + 2}:</span>{' '}
                {s.name}{' '}
                <span className="text-muted-foreground">
                  ({STAFF_TYPE_LABEL[s.staffType] ?? s.staffType})
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <p className="text-muted-foreground">No fallback candidates.</p>
      )}
      {info.staffReason ? (
        <Row label="Why">{info.staffReason}</Row>
      ) : null}
      {info.resourceReason ? (
        <Row label="Resource">{info.resourceReason}</Row>
      ) : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[5rem_1fr] gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span>{children}</span>
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: number;
  sub?: string;
}) {
  return (
    <li className="rounded-2xl border border-brand-sage/15 bg-card p-5 shadow-brand">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 text-3xl font-bold tracking-tight tabular-nums">{value}</p>
      {sub ? <p className="mt-1 text-xs text-muted-foreground">{sub}</p> : null}
    </li>
  );
}

function MembersTable({
  members,
  tenantSlug,
}: {
  members: BusinessDetail['members'];
  tenantSlug: string;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  async function impersonate(userId: string) {
    if (busyUserId) return;
    setError(null);
    setBusyUserId(userId);
    try {
      const res = await authClient.admin.impersonateUser({ userId });
      if ('error' in res && res.error) {
        setError(res.error.message ?? 'Failed to impersonate.');
        setBusyUserId(null);
        return;
      }
      // The admin plugin already set the impersonation cookie. Hard
      // navigate so every cached query reloads under the new session
      // and the ImpersonationBanner picks up the new useSession state.
      window.location.href = `/admin/${tenantSlug}`;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to impersonate.');
      setBusyUserId(null);
    }
  }

  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-brand-sage/15 bg-card shadow-brand">
      {error ? (
        <div className="border-b border-rose-300/60 bg-rose-50 px-4 py-3 text-xs text-rose-900">
          {error}
        </div>
      ) : null}
      <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead className="border-b border-brand-sage/10 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          <tr>
            <th className="px-4 py-3">Person</th>
            <th className="px-4 py-3">Role</th>
            <th className="px-4 py-3" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr
              key={m.userId}
              className="border-b border-brand-sage/10 last:border-b-0"
            >
              <td className="px-4 py-3">
                <span className="block font-medium">{m.name || '—'}</span>
                <span className="block text-xs text-muted-foreground">{m.email}</span>
              </td>
              <td className="px-4 py-3">
                <span className="inline-flex rounded-md border border-border bg-secondary/40 px-2 py-0.5 text-xs font-medium">
                  {formatRole(m.role)}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  {m.role === 'owner' || m.role === 'manager' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyUserId === m.userId}
                      onClick={() => impersonate(m.userId)}
                      className="whitespace-nowrap"
                    >
                      <UserCog className="h-3.5 w-3.5" />
                      {busyUserId === m.userId ? 'Switching…' : 'Impersonate'}
                    </Button>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
